import React, { useEffect } from 'react';
import { X } from '../../bravosIcons';
import { displayNameFromProfile } from '../../../../pages/account/accountShared';
import { toLocalDateKey } from '../../../../utils/dateKey';
import './AssignWorkoutPlanModal.css';

export type AssignWorkoutModalProps = {
  open: boolean;
  assigningWorkoutId: string | null;
  workoutName: string;
  assignMembers: any[];
  loadingAssignMembers: boolean;
  selectedMemberId: string;
  onSelectedMemberIdChange: (id: string) => void;
  workoutDate: string;
  onWorkoutDateChange: (date: string) => void;
  assignmentError: string | null;
  isAssigning: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onAddMember: () => void;
};

function memberLabel(member: { member?: { email?: string | null } | null; memberId: string }): string {
  const profile = member.member as Parameters<typeof displayNameFromProfile>[0] | undefined;
  return displayNameFromProfile(profile) || member.member?.email || 'Unknown Member';
}

function openNativeDatePicker(input: HTMLInputElement) {
  if ('showPicker' in input && typeof input.showPicker === 'function') {
    try {
      input.showPicker();
      return;
    } catch {
      // showPicker may throw if not triggered by user gesture in some browsers
    }
  }
  input.focus();
}

/** Assign workout to a member (Library Workouts tab). */
export function AssignWorkoutModal({
  open,
  assigningWorkoutId,
  workoutName,
  assignMembers,
  loadingAssignMembers,
  selectedMemberId,
  onSelectedMemberIdChange,
  workoutDate,
  onWorkoutDateChange,
  assignmentError,
  isAssigning,
  onClose,
  onSubmit,
  onAddMember,
}: AssignWorkoutModalProps) {
  const todayKey = toLocalDateKey(new Date());
  const canDismiss = !isAssigning;
  const hasMembers = assignMembers.length > 0;
  const submitDisabled =
    isAssigning
    || !selectedMemberId
    || !workoutDate
    || !hasMembers
    || loadingAssignMembers;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && canDismiss) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, canDismiss, onClose]);

  if (!open || !assigningWorkoutId) return null;

  return (
    <div
      className="awp-modal-scrim"
      role="presentation"
      onClick={() => {
        if (canDismiss) onClose();
      }}
    >
      <div
        className="awp-modal tr-dialog--lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="aw-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="awp-modal__header">
          <h2 className="awp-modal__title" id="aw-modal-title">
            Assign to a member
          </h2>
          <button
            type="button"
            className="awp-modal__close"
            aria-label="Close"
            onClick={onClose}
            disabled={!canDismiss}
          >
            <X width={16} height={16} aria-hidden />
          </button>
        </header>

        <div className="awp-modal__body">
          {assignmentError ? (
            <div className="awp-modal__error" role="alert">
              {assignmentError}
            </div>
          ) : null}

          <div className="awp-modal__field">
            <span className="awp-modal__label">Workout</span>
            <div className="awp-modal__readonly">{workoutName}</div>
          </div>

          <div className="awp-modal__field">
            <label className="awp-modal__label" htmlFor="aw-member-select">
              Member
              <span className="awp-modal__required-dot" title="Required" />
            </label>
            {loadingAssignMembers ? (
              <div className="awp-modal__loading">
                <span className="awp-modal__spinner" aria-hidden="true" />
                Loading members…
              </div>
            ) : !hasMembers ? (
              <div className="awp-modal__empty">
                <p>No active members found. Add a member to assign this workout.</p>
                <button type="button" className="awp-modal__empty-cta" onClick={onAddMember}>
                  Add member
                </button>
              </div>
            ) : (
              <div className="awp-modal__select-wrap">
                <select
                  id="aw-member-select"
                  className={`awp-modal__select${selectedMemberId ? ' awp-modal__select--has-value' : ''}`}
                  value={selectedMemberId}
                  onChange={(e) => onSelectedMemberIdChange(e.target.value)}
                  required
                >
                  <option value="">Choose a member…</option>
                  {assignMembers.map((member) => (
                    <option key={member.memberId} value={member.memberId}>
                      {memberLabel(member)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="awp-modal__field">
            <label className="awp-modal__label" htmlFor="aw-workout-date">
              Workout date
              <span className="awp-modal__required-dot" title="Required" />
            </label>
            <input
              id="aw-workout-date"
              type="date"
              className="awp-modal__date"
              value={workoutDate}
              min={todayKey}
              onChange={(e) => onWorkoutDateChange(e.target.value)}
              onFocus={(e) => openNativeDatePicker(e.currentTarget)}
              onClick={(e) => {
                e.stopPropagation();
                openNativeDatePicker(e.currentTarget);
              }}
              required
            />
          </div>
        </div>

        <footer className="awp-modal__footer">
          <button
            type="button"
            className="awp-modal__btn awp-modal__btn--ghost"
            onClick={onClose}
            disabled={!canDismiss}
          >
            Cancel
          </button>
          <button
            type="button"
            className="awp-modal__btn awp-modal__btn--primary"
            onClick={onSubmit}
            disabled={submitDisabled}
          >
            {isAssigning ? (
              <>
                <span className="awp-modal__spinner" aria-hidden="true" />
                Assigning…
              </>
            ) : (
              'Assign workout'
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}
